import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedSkills = ({ featuredSkills }) => {
  const getSkillIcon = (skillName) => {
    const iconMap = {
      'React': 'Atom',
      'Node.js': 'Server',
      'TypeScript': 'Code2',
      'AWS': 'Cloud',
      'Docker': 'Package',
      'MongoDB': 'Database'
    };
    return iconMap[skillName] || 'Code';
  };

  const getSkillColor = (skillName) => {
    const colorMap = {
      'React': 'from-blue-500 to-cyan-500',
      'Node.js': 'from-green-500 to-emerald-500',
      'TypeScript': 'from-blue-600 to-indigo-600',
      'AWS': 'from-orange-500 to-yellow-500',
      'Docker': 'from-blue-400 to-blue-600',
      'MongoDB': 'from-green-600 to-green-800'
    };
    return colorMap[skillName] || 'from-primary to-secondary';
  };

  return (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary mb-4">Featured Expertise</h2>
        <p className="text-lg text-text-secondary max-w-2xl mx-auto">
          Core technologies and frameworks that define my development approach and drive project success.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {featuredSkills.map((skill, index) => (
          <div
            key={skill.name}
            className="group relative bg-card rounded-xl border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg overflow-hidden"
          >
            {/* Gradient Background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${getSkillColor(skill.name)} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
            
            <div className="relative p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${getSkillColor(skill.name)} text-white`}>
                  <Icon name={getSkillIcon(skill.name)} size={24} />
                </div>
                <span className="text-xs font-medium text-text-secondary bg-muted px-2 py-1 rounded-full">
                  {skill.experience}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-text-primary mb-2">{skill.name}</h3>
              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                {skill.description}
              </p>

              {/* Proficiency Indicator */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-primary">Proficiency</span>
                  <span className="text-sm text-text-secondary">{skill.proficiency}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full bg-gradient-to-r ${getSkillColor(skill.name)} transition-all duration-500`}
                    style={{ 
                      width: skill.proficiency === 'Expert' ? '100%' : 
                             skill.proficiency === 'Advanced' ? '85%' : '70%' 
                    }}
                  />
                </div>
              </div>

              {/* Key Projects */}
              {skill.keyProjects && skill.keyProjects.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-text-primary mb-2">Key Projects:</h4>
                  <div className="flex flex-wrap gap-1">
                    {skill.keyProjects.map((project, idx) => (
                      <span 
                        key={idx}
                        className="text-xs bg-muted text-text-secondary px-2 py-1 rounded"
                      >
                        {project}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {skill.certifications && skill.certifications.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center space-x-1 mb-2">
                    <Icon name="Award" size={14} className="text-accent" />
                    <span className="text-sm font-medium text-text-primary">Certifications</span>
                  </div>
                  <div className="space-y-1">
                    {skill.certifications.map((cert, idx) => (
                      <div key={idx} className="text-xs text-text-secondary">
                        {cert}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Button */}
              <Link to="/projects" className="block">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                  className="w-full group-hover:border-primary group-hover:text-primary transition-colors duration-200"
                >
                  View Projects
                </Button>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedSkills;